/**
 * frontend/src/pages/valuation/_operatingAgreement.ts
 *
 * VAL-OA-SCREEN-1 — what the operating agreement reading offers the Company
 * step. Pure: the reading in, rows out. Nothing here saves; the page puts the
 * rows in the form and the existing Save persists them.
 *
 * Names are masked on the way in: a run of 6 or more digits (an account-like
 * number inside a trust line) is shown as ****NNNN, last 4 only. Two
 * spellings of one person are two rows; the user removes one. Ownership % is
 * filled from the agreement as an ordinary editable value; one that cannot be
 * read as a number is left blank.
 */
import type { OperatingAgreementReading } from '@/api/valuationFinancials'
import { joinName, splitName, type CompanyState, type PersonRow } from './_companyProfile'

export interface Offered {
  name: string
  /** Title as written in the agreement; '' for an owner. */
  role: string
  /** "From <file name>, page <n>" (page omitted when the reading has none). */
  source: string
  /** Ownership % as a plain number string ('7.69'), '' when not readable; owners only. */
  pct: string
}

export interface AgreementOffer {
  /** File names of readings that can be used. */
  readFiles: string[]
  /** True when an agreement was found but none could be read. */
  notRead: boolean
  owners: Offered[]
  people: Offered[]
}

export const NO_OFFER: AgreementOffer = { readFiles: [], notRead: false, owners: [], people: [] }

/** '7.69%' -> '7.69', '0%' -> '0'; anything that is not a plain number -> ''. */
export function parsePct(raw: string | null | undefined): string {
  const m = /^\s*(\d+(?:\.\d+)?|\.\d+)\s*%?\s*$/.exec(raw ?? '')
  return m ? String(Number(m[1])) : ''
}

export function maskDigits(name: string): string {
  return name.replace(/\d{6,}/g, run => `****${run.slice(-4)}`)
}

export const nameKey = (s: string): string => s.split(/\s+/).filter(Boolean).join(' ').toLowerCase()

function source(filename: string, page: number | null | undefined): string {
  return page ? `From ${filename}, page ${page}` : `From ${filename}`
}

export function offerFromReadings(
  agreements: Record<string, OperatingAgreementReading> | null | undefined,
): AgreementOffer {
  const readings = Object.values(agreements ?? {})
  if (!readings.length) return NO_OFFER
  const usable = readings.filter(r => r.status === 'read' || r.status === 'partly_read')
  if (!usable.length) return { ...NO_OFFER, notRead: true }
  const owners: Offered[] = []
  const people: Offered[] = []
  for (const r of usable) {
    const file = r.filename
    for (const m of r.members ?? []) {
      const name = maskDigits((m.name ?? '').trim())
      if (!name) continue
      owners.push({ name, role: '', source: source(file, m.page), pct: parsePct(m.membership_interest_pct) })
    }
    for (const p of r.managers_officers ?? []) {
      const name = maskDigits((p.name ?? '').trim())
      if (!name) continue
      people.push({ name, role: (p.title ?? '').trim(), source: source(file, p.page), pct: '' })
    }
  }
  return { readFiles: usable.map(r => r.filename), notRead: false, owners, people }
}

/** Add the offered names to the form. Typed rows stay; a name already there is not added again. */
export function applyOffer(form: CompanyState, offer: AgreementOffer): CompanyState {
  const blankOwner = (o: { name: string; pct: string }) => o.name.trim() === '' && o.pct.trim() === ''
  const blankPerson = (k: PersonRow) => k.first.trim() === '' && k.last.trim() === '' && k.role.trim() === '' && k.contact.trim() === ''
  const owners = form.owners.filter(o => !blankOwner(o)).map(o => ({ ...o }))
  const people = form.key_people.filter(k => !blankPerson(k))
  const haveOwner = new Set(owners.map(o => nameKey(o.name)))
  const filled = new Map(owners.map(o => [nameKey(o.name), o]))
  const havePerson = new Set(people.map(k => nameKey(joinName(k.first, k.last))))
  for (const o of offer.owners) {
    const key = nameKey(o.name)
    if (haveOwner.has(key)) {
      // A % the user typed stays; only a blank one takes the agreement's.
      const row = filled.get(key)
      if (row && row.pct.trim() === '') row.pct = o.pct
      continue
    }
    haveOwner.add(key)
    const row = { name: o.name, pct: o.pct }
    filled.set(key, row)
    owners.push(row)
  }
  for (const p of offer.people) {
    const key = nameKey(p.name)
    if (havePerson.has(key)) continue
    havePerson.add(key)
    people.push({ ...splitName(p.name), role: p.role, contact: '' })
  }
  return { ...form, owners, key_people: people }
}
